import React, { useMemo, useRef, useState } from "react";
import { Zap, Truck, Clock, ShieldCheck, HeartHandshake, CircleDollarSign, ArrowRight, MessageCircle, Sparkles, ChevronLeft, ChevronRight, MessageSquareText } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { imgUrl } from "../../lib/api";
import { Container, Reveal, SectionHead } from "../site/Reveal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const FEATURES = [
  [Zap, "Clear Updates", "We let you know when your box arrives and when your payment is sent."],
  [Truck, "Free Shipping", "A prepaid label comes with every accepted quote."],
  [Clock, "Real People", "Call, text or chat, and a real team member will help you."],
  [ShieldCheck, "Honest & Private", "Your details stay private, and every step is explained."],
  [HeartHandshake, "Less Waste", "Sealed supplies get a second life instead of going in the trash."],
  [CircleDollarSign, "Fair Offers", "Clear offers based on quantity, condition and dates."],
];

export const WhyChoose = () => {
  const { content, openChat, t } = useSite();
  return (
    <section className="bg-[#f4fbf9] py-20 md:py-24" data-testid="why-choose">
      <Container>
        <SectionHead eyebrow={t("Do your boxes fit the bill?")} title={t("Why Choose {brand}?", { brand: content?.brand })} text={t("Friendly service, clear offers and free shipping, from your first message to your final payment.")} />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(([I, title, d], idx) => (
            <Reveal key={title} delay={(idx % 3) * 100} className="sw-card flex gap-4 p-6">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#d3f6ee] text-[#0e8c7f]"><I size={19} /></span>
              <div>
                <h3 className="text-[18px] font-bold text-[#0f2e2a]">{t(title)}</h3>
                <p className="mt-1.5 text-[15px] leading-relaxed text-[#566865]">{t(d)}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-12 flex flex-wrap justify-center gap-3">
          <button onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })} className="sw-btn-primary" data-testid="why-sell-now">{t("Sell Now")} <ArrowRight size={16} /></button>
          <button onClick={() => openChat()} className="sw-btn-outline" data-testid="why-chat"><MessageCircle size={16} /> {t("Chat With Us")}</button>
        </div>
      </Container>
    </section>
  );
};

export const Brands = ({ heading = true }) => {
  const { content, openChat, t } = useSite();
  const brands = content?.brands || [];
  return (
    <section className="py-20 md:py-24" data-testid="brands-section">
      <Container>
        {heading && <SectionHead center={false} eyebrow={t("We Buy in Bulk")} title={t("Manufacturer Brands")} text={t("We accept sealed, unexpired supplies from many well-known brands. Tap a brand to ask for a quote.")} />}
        <div className="mt-12 grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-4">
          {brands.map((b, idx) => (
            <Reveal key={b.name} delay={(idx % 4) * 80} className="h-full">
              <div className="sw-card group flex h-full w-full flex-col p-4 md:p-5 text-center" data-testid={`brand-card-${idx}`}>
                <button onClick={() => openChat(t("Hi! I'd like a quote for my {name} supplies.", { name: b.name }))} className="block w-full" aria-label={`Get a quote for ${b.name}`}>
                  <div className="sw-watermark flex h-[130px] md:h-[170px] items-center justify-center rounded-xl bg-white">
                    <img src={imgUrl(b.image)} alt={`${b.name} supplies we accept`} className="max-h-full max-w-[85%] object-contain transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  </div>
                  <div className="mt-4 md:mt-5 font-jakarta text-[16px] md:text-[18px] font-semibold text-[#0f2e2a]">{b.name}</div>
                </button>
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <button onClick={() => openChat(t("Hi! I'd like a quote for my {name} supplies.", { name: b.name }))} className="flex items-center justify-center gap-1.5 rounded-full bg-[#0e8c7f] px-3 py-2.5 text-[13.5px] font-semibold text-white hover:bg-[#0b7569] transition-colors" data-testid={`brand-chat-btn-${idx}`}>
                    <MessageCircle size={15} /> {t("Chat with us")}
                  </button>
                  <a href={`sms:${content?.phone_raw}?body=${encodeURIComponent(t("Hi StripWise! I'd like a quote for my {name} supplies.", { name: b.name }))}`} className="flex items-center justify-center gap-1.5 rounded-full border border-[#0e8c7f] px-3 py-2.5 text-[13.5px] font-semibold text-[#0e8c7f] hover:bg-[#effaf7] transition-colors" data-testid={`brand-text-btn-${idx}`}>
                    <MessageSquareText size={15} /> {t("Text us")}
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-8 text-center text-[14px] text-[#8a9a97]">{t("All product photos © {brand}. Don't see your brand? Chat with us for a custom quote.", { brand: content?.brand })}</p>
      </Container>
    </section>
  );
};

export const ProductCard = ({ p, onChat }) => {
  const { content, t } = useSite();
  const msg = encodeURIComponent(t("Hi StripWise! I'd like a quote for {name} ({size}).", { name: p.name, size: p.size }));
  return (
  <div className="sw-card flex h-full flex-col overflow-hidden" data-testid="product-card">
    <div className="sw-watermark relative flex h-[200px] items-center justify-center bg-white p-5">
      <img src={imgUrl(p.image)} alt={p.name} className="max-h-full max-w-[85%] object-contain" loading="lazy" />
      {p.badge && <span className="absolute left-3 top-3 z-10 rounded-full bg-[#f59e0b] px-2.5 py-1 text-[11px] font-bold text-white">{p.badge}</span>}
      <span className={`absolute right-3 top-3 z-10 rounded-full px-2.5 py-1 text-[11px] font-semibold ${p.in_stock ? "bg-[#dcf7ef] text-[#0b6f65]" : "bg-[#f1f1f1] text-[#777]"}`}>{t(p.in_stock ? "In stock" : "Paused")}</span>
    </div>
    <div className="flex flex-1 flex-col border-t border-[#eef5f3] p-5">
      <span className="text-[12px] font-semibold uppercase tracking-wider text-[#0e8c7f]">{p.brand}</span>
      <h3 className="mt-1 text-[17px] font-bold leading-snug text-[#0f2e2a]">{p.name}</h3>
      <p className="mt-1 text-[13.5px] text-[#6b7c79]">{[p.size, p.expiry].filter(Boolean).join(" · ")}</p>
      <div className="mt-auto flex items-center justify-between pt-4">
        <span className="font-jakarta text-[20px] font-extrabold text-[#0f2e2a]">{t("Up to")} ${Number(p.price).toLocaleString()}</span>
      </div>
      <button onClick={() => onChat(p)} className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-full bg-[#0e8c7f] py-2.5 text-[14px] font-semibold text-white hover:bg-[#0b7569] transition-colors" data-testid="product-chat-btn">
        <MessageCircle size={15} /> {t("Chat with us")}
      </button>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <a href={`sms:${content?.phone_raw}?body=${msg}`} className="flex items-center justify-center gap-1.5 rounded-full border border-[#0f2e2a] py-2 text-[13.5px] font-semibold text-[#0f2e2a] hover:bg-[#0f2e2a] hover:text-white transition-colors" data-testid="product-text-btn">
          <MessageSquareText size={14} /> {t("Text us")}
        </a>
        <a href={`https://wa.me/${content?.whatsapp_raw}?text=${msg}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 rounded-full bg-[#25a35a] py-2 text-[13.5px] font-semibold text-white hover:bg-[#1e8a4b] transition-colors" data-testid="product-whatsapp-btn">
          <MessageCircle size={14} /> WhatsApp
        </a>
      </div>
    </div>
  </div>
  );
};

export const Supplies = () => {
  const { content, products, openChat, t } = useSite();
  const [brand, setBrand] = useState("All");
  const [sort, setSort] = useState("high");
  const track = useRef(null);
  const deal = content?.deal;
  const brands = useMemo(() => ["All", ...Array.from(new Set(products.map((p) => p.brand)))], [products]);
  const list = useMemo(() => {
    let l = brand === "All" ? [...products] : products.filter((p) => p.brand === brand);
    if (sort === "high") l.sort((a, b) => b.price - a.price);
    if (sort === "low") l.sort((a, b) => a.price - b.price);
    if (sort === "name") l.sort((a, b) => a.name.localeCompare(b.name));
    return l;
  }, [products, brand, sort]);
  const scroll = (d) => track.current?.scrollBy({ left: d * (track.current.clientWidth * 0.8), behavior: "smooth" });
  const chat = (p) => openChat(t("Hi! I'd like a quote for {name} ({size}).", { name: p.name, size: p.size }));

  return (
    <section id="supplies" className="bg-[#f4fbf9] py-20 md:py-24" data-testid="supplies-section">
      <Container>
        <SectionHead eyebrow={t("What We Buy")} title={t("Supplies We Purchase")} text={t("Browse the items we accept, filter by brand, and tap any item to ask for a quote.", { brand: content?.brand })} />

        {deal?.enabled && (
          <Reveal className="mt-12 flex flex-col gap-5 rounded-[26px] bg-[linear-gradient(120deg,#0e8c7f,#0d645c)] p-6 md:flex-row md:items-center md:p-7" data-testid="deal-banner">
            <div className="sw-watermark h-24 w-24 shrink-0 rounded-2xl bg-white p-2">
              <img src={imgUrl(deal.image)} alt={deal.title} className="h-full w-full object-contain" />
            </div>
            <div className="flex-1 text-white">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#fbbf24] px-3 py-1 text-[11.5px] font-bold uppercase tracking-wide text-[#3b2a03]"><Sparkles size={12} />{deal.label}</span>
              <h3 className="mt-2 font-jakarta text-[26px] md:text-[30px] font-bold">{deal.title}</h3>
              <p className="mt-1 text-[16.5px] text-white/85">{t("Featured item this week —")} <b className="text-white">{deal.amount}</b> {deal.text}</p>
            </div>
            <button onClick={() => openChat(t("Hi! I'm interested in the {title} deal ({amount}).", { title: deal.title, amount: deal.amount }))} className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-[14.5px] font-semibold text-[#0b6f65] hover:bg-[#effaf7] transition-colors" data-testid="deal-chat-btn">
              <MessageCircle size={15} /> {t("Chat About This Deal")}
            </button>
          </Reveal>
        )}

        <div className="mt-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="no-scrollbar flex gap-2 overflow-x-auto pb-1">
            {brands.map((b) => (
              <button key={b} onClick={() => setBrand(b)} className={`shrink-0 rounded-full border px-4 py-2 text-[14px] font-semibold transition-colors ${brand === b ? "border-[#0e8c7f] bg-[#0e8c7f] text-white" : "border-[#dcebe8] bg-white text-[#1f3a36] hover:border-[#0e8c7f]"}`} data-testid={`filter-${b}`}>
                {b === "All" ? t("All") : b}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[14px] text-[#5b6d6a]">{t("Sort by")}</span>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[180px] rounded-full bg-white" data-testid="sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="high">{t("Offer: high to low")}</SelectItem>
                <SelectItem value="low">{t("Offer: low to high")}</SelectItem>
                <SelectItem value="name">{t("Name A–Z")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="relative mt-8">
          <div ref={track} className="no-scrollbar flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4" data-testid="supplies-track">
            {list.map((p) => (
              <div key={p.id} className="w-[78%] shrink-0 snap-start sm:w-[46%] lg:w-[calc(25%-15px)]">
                <ProductCard p={p} onChat={chat} />
              </div>
            ))}
          </div>
          <button onClick={() => scroll(-1)} className="absolute -left-4 top-[40%] hidden md:flex h-11 w-11 items-center justify-center rounded-full border border-[#dcebe8] bg-white text-[#0f2e2a] shadow-lg hover:bg-[#0e8c7f] hover:text-white transition-colors" aria-label="Previous slide" data-testid="supplies-prev"><ChevronLeft size={20} /></button>
          <button onClick={() => scroll(1)} className="absolute -right-4 top-[40%] hidden md:flex h-11 w-11 items-center justify-center rounded-full border border-[#dcebe8] bg-white text-[#0f2e2a] shadow-lg hover:bg-[#0e8c7f] hover:text-white transition-colors" aria-label="Next slide" data-testid="supplies-next"><ChevronRight size={20} /></button>
        </div>
        <p className="mt-6 text-center text-[14px] text-[#8a9a97]">{t("All product photos © {brand}. Swipe to see more · Don't see your item? Chat with us for a custom quote.", { brand: content?.brand })}</p>
      </Container>
    </section>
  );
};
